import axios from "axios"
import firebase from 'firebase/app'
import 'firebase/auth'
import base64js from 'base64-js';
import sjcl from "sjcl"

const ALGORITHM_NAME = "AES-GCM";
const ALGORITHM_NONCE_SIZE = 12;
const ALGORITHM_KEY_SIZE = 16 * 8;
const PBKDF2_SALT_SIZE = 16;
const PBKDF2_ITERATIONS = 32767;

var sjclBITS_PER_WORD = 32;
var sjclALGORITHM_NONCE_SIZE = 3; // 32-bit words.
var sjclALGORITHM_KEY_SIZE = 4; // 32-bit words.
var sjclPBKDF2_SALT_SIZE = 4; // 32-bit words.
var sjclPBKDF2_ITERATIONS = 32767;

async function sjclencryptString(plaintext, password) {
  // Generate a 128-bit salt using a CSPRNG.
  var salt = sjcl.random.randomWords(sjclPBKDF2_SALT_SIZE);

  // Derive a key using PBKDF2.
  var key = sjcl.misc.pbkdf2(password, salt, sjclPBKDF2_ITERATIONS, sjclALGORITHM_KEY_SIZE * sjclBITS_PER_WORD);

  // Encrypt and prepend salt.
  var plaintextRaw = sjcl.codec.utf8String.toBits(plaintext);
  var ciphertextAndNonceAndSalt = sjcl.bitArray.concat(salt, sjclencrypt(plaintextRaw, key));

  return sjcl.codec.base64.fromBits(ciphertextAndNonceAndSalt);
}

async function sjcldecryptString(base64CiphertextAndNonceAndSalt, password) {
  // Decode the base64.
  var ciphertextAndNonceAndSalt = sjcl.codec.base64.toBits(base64CiphertextAndNonceAndSalt);

  // Create buffers of salt and ciphertextAndNonce.
  var salt = sjcl.bitArray.bitSlice(ciphertextAndNonceAndSalt, 0, sjclPBKDF2_SALT_SIZE * sjclBITS_PER_WORD);
  var ciphertextAndNonce = sjcl.bitArray.bitSlice(ciphertextAndNonceAndSalt, sjclPBKDF2_SALT_SIZE * sjclBITS_PER_WORD);

  // Derive the key using PBKDF2.
  var key = sjcl.misc.pbkdf2(password, salt, sjclPBKDF2_ITERATIONS, sjclALGORITHM_KEY_SIZE * sjclBITS_PER_WORD);

  // Decrypt and return result.
  return sjcl.codec.utf8String.fromBits(sjcldecrypt(ciphertextAndNonce, key));
}

function sjclencrypt(plaintext, key) {
  // Generate a 96-bit nonce using a CSPRNG.
  var nonce = sjcl.random.randomWords(sjclALGORITHM_NONCE_SIZE);

  // Encrypt and prepend nonce.
  var ciphertext = sjcl.mode.gcm.encrypt(new sjcl.cipher.aes(key), plaintext, nonce);

  return sjcl.bitArray.concat(nonce, ciphertext);
}

function sjcldecrypt(ciphertextAndNonce, key) {
  // Create buffers of nonce and ciphertext.
  var nonce = sjcl.bitArray.bitSlice(ciphertextAndNonce, 0, sjclALGORITHM_NONCE_SIZE * sjclBITS_PER_WORD);
  var ciphertext = sjcl.bitArray.bitSlice(ciphertextAndNonce, sjclALGORITHM_NONCE_SIZE * sjclBITS_PER_WORD);

  // Decrypt and return result.
  return sjcl.mode.gcm.decrypt(new sjcl.cipher.aes(key), ciphertext, nonce);
}


async function encryptString(plaintext, password) {
  // Generate a 128-bit salt using a CSPRNG and a nonce.
  let salt = crypto.getRandomValues(new Uint8Array(PBKDF2_SALT_SIZE));
  let nonce = crypto.getRandomValues(new Uint8Array(ALGORITHM_NONCE_SIZE));
  let aesGcm = {name: ALGORITHM_NAME, iv: nonce};

  // Derive a key using PBKDF2.
  let deriveParams = {name: "PBKDF2", salt: salt, iterations: PBKDF2_ITERATIONS, hash: {name: "SHA-256"}};
  let rawKey = await crypto.subtle.importKey("raw", (new TextEncoder()).encode(password), {name: "PBKDF2"}, false, ["deriveKey", "deriveBits"]);
  let cryptoKey = await crypto.subtle.deriveKey(deriveParams, rawKey, {
    name: ALGORITHM_NAME,
    length: ALGORITHM_KEY_SIZE
  }, true, ["encrypt"]);

  // Encrypt the string.
  let ciphertext = await encryptWithCryptoKey(aesGcm, (new TextEncoder()).encode(plaintext), cryptoKey);
  return base64js.fromByteArray(joinBuffers(salt, ciphertext));
}

async function decryptString(base64CiphertextAndNonceAndSalt, password) {
  // Decode the base64.
  let ciphertextAndNonceAndSalt = base64js.toByteArray(base64CiphertextAndNonceAndSalt);

  // Create buffers of salt and ciphertextAndNonce.
  let salt = ciphertextAndNonceAndSalt.slice(0, PBKDF2_SALT_SIZE);
  let nonce = ciphertextAndNonceAndSalt.slice(PBKDF2_SALT_SIZE, PBKDF2_SALT_SIZE + ALGORITHM_NONCE_SIZE);
  let ciphertext = ciphertextAndNonceAndSalt.slice(PBKDF2_SALT_SIZE + ALGORITHM_NONCE_SIZE);
  let aesGcm = {name: ALGORITHM_NAME, iv: nonce};

  // Derive the key using PBKDF2.
  let deriveParams = {name: "PBKDF2", salt: salt, iterations: PBKDF2_ITERATIONS, hash: {name: "SHA-256"}};
  let rawKey = await crypto.subtle.importKey("raw", (new TextEncoder()).encode(password), {name: "PBKDF2"}, false, ["deriveKey", "deriveBits"]);
  let cryptoKey = await crypto.subtle.deriveKey(deriveParams, rawKey, {
    name: ALGORITHM_NAME,
    length: ALGORITHM_KEY_SIZE
  }, true, ["decrypt"]);

  // Decrypt the string.
  let plaintext = await decryptWithCryptoKey(aesGcm, ciphertext, cryptoKey);
  return (new TextDecoder()).decode(plaintext);
}

async function encrypt(plaintext, key) {
  // Generate a 96-bit nonce using a CSPRNG.
  let nonce = crypto.getRandomValues(new Uint8Array(ALGORITHM_NONCE_SIZE));
  let aesGcm = {name: ALGORITHM_NAME, iv: nonce};

  // Create a 'CryptoKey'.
  let cryptoKey = await crypto.subtle.importKey("raw", key, aesGcm, false, ["encrypt"]);

  // Encrypt.
  return await encryptWithCryptoKey(aesGcm, plaintext, cryptoKey);
}

async function encryptWithCryptoKey(aesGcm, plaintext, cryptoKey) {
  let ciphertext = await crypto.subtle.encrypt(aesGcm, cryptoKey, plaintext);
  return joinBuffers(aesGcm.iv, new Uint8Array(ciphertext));
}

async function decrypt(ciphertextAndNonce, key) {
  // Create buffers of the nonce and ciphertext.
  let nonce = ciphertextAndNonce.slice(0, ALGORITHM_NONCE_SIZE);
  let ciphertext = ciphertextAndNonce.slice(ALGORITHM_NONCE_SIZE);

  let aesGcm = {name: ALGORITHM_NAME, iv: nonce};

  // Create the 'CryptoKey'.
  let cryptoKey = await crypto.subtle.importKey("raw", key, aesGcm, false, ["decrypt"]);

  // Decrypt.
  return await decryptWithCryptoKey(aesGcm, ciphertext, cryptoKey)
}

async function decryptWithCryptoKey(aesGcm, ciphertext, cryptoKey) {
  let plaintext = await crypto.subtle.decrypt(aesGcm, cryptoKey, ciphertext);
  return new Uint8Array(plaintext);
}

function joinBuffers(a, b) {
  let c = new Uint8Array(a.byteLength + b.byteLength);

  for (let i = 0; i < a.length; i++) {
    c[i] = a[i];
  }
  for (let i = 0; i < b.length; i++) {
    c[i + a.length] = b[i];
  }

  return c;
}

function e(text, pa) {
  if (window.navigator.userAgent.indexOf("Edge") > 0) {
    return sjclencryptString(text, pa);
  } else {
    return encryptString(text, pa);
  }
}

function de(text, pa) {
  if (window.navigator.userAgent.indexOf("Edge") > -1) {
    return sjcldecryptString(text, pa);
  } else {
    return decryptString(text, pa);
  }
}

let BASE_URL = "https://forwardchessbackend.appspot.com/";
var pa = undefined;
var lastToken = undefined;

function loadMeta(id, chap, metaUrl, metaBody, callback, isEnc) {
  let key = encodeURI(btoa(id + ":" + chap));

  firebase.auth().signInAnonymously().then(user => {
    firebase.auth().currentUser.getIdToken(false).then(function (idToken) {

      if (pa && idToken === lastToken) {
        loadMetaPa(pa, key, metaUrl, metaBody, callback, idToken, isEnc);
      } else {
        let url = BASE_URL + "metapa/" + key;
        axios.post(url, "empty",
          {
            headers: {
              "Authorization": idToken
            }
          }
        ).then((response) => {
          pa = response.data;
          lastToken = idToken;
          loadMetaPa(pa, key, metaUrl, metaBody, callback, idToken, isEnc);
        }).catch((error) => {
          console.log(error)
        });
      }
    });
  });
}

function loadMetaPa(pa, key, metaUrl, metaBody, callback, idToken, isEnc) {

  e(metaBody, pa).then(value => {

    axios.post(metaUrl + key + "?sample=true", value,
      {
        headers: {
          "Authorization": idToken,
          "Content-Type": 'text/plain'
        }
      }
    ).then((response) => {

      if (isEnc) {
        de(response.data, pa).then(value => {
          callback(value);
        });
      } else {
        callback(response.data);
      }
    }).catch((error) => {
      console.log(error)
    });
  }).catch(error => {
    console.log(error);
  });

}

export default {
  loadBookMappings({commit, state, dispatch}, book) {

    loadMeta(book.id.toLowerCase(), "0", BASE_URL + "metabm/", "empty", function (response) {
      let bm = JSON.parse(response);
      commit("updateBookMappings", bm);

      if (!state.chapterContent) {
        let chapter = {
          bookId: book.id,
          file: book.chapterFile !== undefined ? book.chapterFile : bm.cover.file,
          sample: state.sample
        };

        if (localStorage.getItem("history")) {
          var history = JSON.parse(localStorage.getItem("history"));
          history.bookId = chapter.bookId;
          history.file = chapter.file;
          history.sample = state.sample;
          localStorage.setItem('history', JSON.stringify(history));
        } else {
          localStorage.setItem('history', JSON.stringify(chapter));
        }

        dispatch('loadChapterContent', chapter);
        dispatch('loadToc', {
          bookId: book.id,
          file: bm.toc.file
        });
      }
    },true);
  },
  loadChapterContent({commit,dispatch}, chapter) {
    commit("setChapterContent", "<img style='max-width: 200px;height: auto;float: left;display: block' src=\"loading-gif-icon-9.jpg\"/>");

    loadMeta(chapter.bookId.toLowerCase(), chapter.file, BASE_URL + "metabcon/", "empty", function (response) {
      commit("setChapter", chapter);
      commit("setChapterContent", response);

      if( !chapter.file.startsWith("0") ){
        dispatch('loadBookMoves', chapter);
      }

    },true);

  },
  loadToc({commit}, chapter) {

    loadMeta(chapter.bookId.toLowerCase(), chapter.file, BASE_URL + "metabcon/", "empty", function (response) {
      commit("updateToc", response);
    },true);

  },
  loadBookMoves({commit}, data) {

    loadMeta(data.bookId.toLowerCase(), data.file, BASE_URL + "metabmov/", "empty", function (response) {
      commit("updateBookMoves", JSON.parse(response) );
    },true);

  }
}
